/**
 * TimerBorder Component for Poker Reborn
 *
 * Draws a depleting timer line that traces the perimeter of the PlayerInfoBar's
 * rounded rectangle, with trailing particles at the leading edge. Uses
 * Ticker.shared for smooth frame-by-frame interpolation.
 *
 * The perimeter is pre-computed as a polyline (no arc() calls) to avoid
 * triangle artifacts on mobile GPUs.
 *
 * @see docs/screens/room/SEAT.md - Parent SeatView component
 * @see docs/screens/room/PLAYER_INFO_BAR.md - PlayerInfoBar dimensions
 */

import { Container, Graphics, Ticker } from 'pixi.js';
import { getScaledValue } from '@/utils/responsive';

// =============================================================================
// BBS BREAKPOINTS
// =============================================================================

const TIMER_BORDER_BREAKPOINTS = {
  /** Stroke thickness of the timer line */
  lineWidth: { 375: 3, 720: 3.75, 1440: 4.5, 1920: 5.25 },

  /** Radius of the leading particle */
  particleSize: { 375: 2, 720: 2.5, 1440: 3, 1920: 4 },

  /** Number of trailing particles */
  particleCount: { 375: 3, 720: 4, 1440: 5, 1920: 5 },

  /** Spacing in pixels between trailing particles */
  particleSpacing: { 375: 3, 720: 3.5, 1440: 4, 1920: 5 },
};

// =============================================================================
// COLORS
// =============================================================================

/** Color thresholds matching the original timerRing */
const COLOR_THRESHOLDS: { above: number; color: number }[] = [
  { above: 0.6, color: 0x4caf50 }, // Green
  { above: 0.3, color: 0xffeb3b }, // Yellow
  { above: 0.15, color: 0xff9800 }, // Orange
  { above: 0.0, color: 0xf44336 }, // Red
];

/** Per-particle alpha/size decay */
const PARTICLE_ALPHA_DECAY = 0.22;
const PARTICLE_SIZE_DECAY = 0.15;

/** Number of line segments per quarter-circle corner */
const ARC_RESOLUTION = 8;

// =============================================================================
// POLYLINE POINT
// =============================================================================

interface PerimeterPoint {
  x: number;
  y: number;
  /** Cumulative distance from the start of the perimeter */
  distance: number;
}

// =============================================================================
// TIMER BORDER
// =============================================================================

export interface TimerBorderOptions {
  screenWidth?: number;
}

export class TimerBorder extends Container {
  private borderGraphics: Graphics;
  private particleContainer: Container;
  private particles: Graphics[] = [];
  private glowGraphic: Graphics;

  private currentScreenWidth: number;
  private targetProgress = 1;
  private displayProgress = 1;
  private isAnimating = false;

  // Cached geometry from PlayerInfoBar
  private rectWidth = 0;
  private rectHeight = 0;
  private rectCornerRadius = 0;

  // Pre-computed polyline around the perimeter
  private points: PerimeterPoint[] = [];
  private totalPerimeter = 0;

  constructor(options: TimerBorderOptions = {}) {
    super();

    this.currentScreenWidth = options.screenWidth ?? 1920;

    // Border stroke layer
    this.borderGraphics = new Graphics();
    this.addChild(this.borderGraphics);

    // Particle layer (on top of border)
    this.particleContainer = new Container();
    this.addChild(this.particleContainer);

    // Glow behind leading particle (larger semi-transparent circle)
    this.glowGraphic = new Graphics();
    this.glowGraphic.visible = false;
    this.particleContainer.addChild(this.glowGraphic);

    // Pre-allocate particles
    this.createParticles();
  }

  // ===========================================================================
  // BBS HELPER
  // ===========================================================================

  private bbs(breakpoints: Record<number, number>): number {
    return getScaledValue(this.currentScreenWidth, breakpoints, {
      discrete: true,
    });
  }

  // ===========================================================================
  // PUBLIC API
  // ===========================================================================

  /** Set the target progress (0 = empty, 1 = full). Called on each timerTick. */
  setProgress(progress: number, snap = false): void {
    this.targetProgress = Math.max(0, Math.min(1, progress));
    if (snap) {
      this.displayProgress = this.targetProgress;
    }
  }

  /** Start animating (called when seat becomes active). */
  show(): void {
    this.visible = true;
    if (!this.isAnimating) {
      this.isAnimating = true;
      Ticker.shared.add(this.onTick, this);
    }
  }

  /** Stop animating and clear visuals (called when seat becomes inactive). */
  hide(): void {
    this.visible = false;
    if (this.isAnimating) {
      this.isAnimating = false;
      Ticker.shared.remove(this.onTick, this);
    }
    this.borderGraphics.clear();
    this.hideParticles();
  }

  /** Sync geometry with PlayerInfoBar dimensions. */
  updateGeometry(
    width: number,
    height: number,
    cornerRadius: number
  ): void {
    if (
      width === this.rectWidth &&
      height === this.rectHeight &&
      cornerRadius === this.rectCornerRadius
    ) {
      return;
    }

    this.rectWidth = width;
    this.rectHeight = height;
    this.rectCornerRadius = cornerRadius;
    this.buildPolyline();
  }

  /** Resize for new screen width. */
  resize(screenWidth: number): void {
    if (screenWidth === this.currentScreenWidth) return;
    this.currentScreenWidth = screenWidth;

    // Recreate particles with new BBS sizes
    this.destroyParticles();
    this.createParticles();
  }

  override destroy(options?: Parameters<Container['destroy']>[0]): void {
    if (this.isAnimating) {
      Ticker.shared.remove(this.onTick, this);
      this.isAnimating = false;
    }
    super.destroy(options);
  }

  // ===========================================================================
  // POLYLINE COMPUTATION
  // ===========================================================================

  /**
   * Pre-compute the entire rounded rect perimeter as a polyline.
   * Corners are approximated with ARC_RESOLUTION line segments each.
   * No arc() calls needed at draw time — just moveTo + lineTo.
   */
  private buildPolyline(): void {
    const w = this.rectWidth;
    const h = this.rectHeight;
    const r = Math.min(this.rectCornerRadius, w / 2, h / 2);

    const halfW = w / 2;
    const halfH = h / 2;

    this.points = [];
    let cumulativeDistance = 0;

    const addPoint = (x: number, y: number): void => {
      const lastPoint = this.points[this.points.length - 1];
      if (lastPoint) {
        const dx = x - lastPoint.x;
        const dy = y - lastPoint.y;
        cumulativeDistance += Math.sqrt(dx * dx + dy * dy);
      }
      this.points.push({ x, y, distance: cumulativeDistance });
    };

    // Helper: add an arc as N line segments
    const addArc = (
      centerX: number,
      centerY: number,
      radius: number,
      startAngle: number,
      endAngle: number
    ): void => {
      for (let i = 1; i <= ARC_RESOLUTION; i++) {
        const fraction = i / ARC_RESOLUTION;
        const angle = startAngle + (endAngle - startAngle) * fraction;
        addPoint(
          centerX + Math.cos(angle) * radius,
          centerY + Math.sin(angle) * radius
        );
      }
    };

    // Start: top-center
    addPoint(0, -halfH);

    // Seg 0: top center → top-right corner start
    if (w > 2 * r) {
      addPoint(halfW - r, -halfH);
    }

    // Seg 1: top-right corner arc (-PI/2 → 0)
    if (r > 0) {
      addArc(halfW - r, -halfH + r, r, -Math.PI / 2, 0);
    }

    // Seg 2: right edge
    if (h > 2 * r) {
      addPoint(halfW, halfH - r);
    }

    // Seg 3: bottom-right corner arc (0 → PI/2)
    if (r > 0) {
      addArc(halfW - r, halfH - r, r, 0, Math.PI / 2);
    }

    // Seg 4: bottom edge right → left
    if (w > 2 * r) {
      addPoint(-halfW + r, halfH);
    }

    // Seg 5: bottom-left corner arc (PI/2 → PI)
    if (r > 0) {
      addArc(-halfW + r, halfH - r, r, Math.PI / 2, Math.PI);
    }

    // Seg 6: left edge bottom → top
    if (h > 2 * r) {
      addPoint(-halfW, -halfH + r);
    }

    // Seg 7: top-left corner arc (PI → 3PI/2)
    if (r > 0) {
      addArc(-halfW + r, -halfH + r, r, Math.PI, (3 * Math.PI) / 2);
    }

    // Seg 8: top-left corner end → top center (close the loop)
    if (w > 2 * r) {
      addPoint(0, -halfH);
    }

    this.totalPerimeter = cumulativeDistance;
  }

  // ===========================================================================
  // POINT ON PERIMETER
  // ===========================================================================

  /** Get interpolated (x, y) at a given distance along the perimeter. */
  private getPointAtDistance(distance: number): { x: number; y: number } {
    if (this.points.length === 0) {
      return { x: 0, y: 0 };
    }

    // Clamp
    if (distance <= 0) {
      const firstPoint = this.points[0];
      return { x: firstPoint?.x ?? 0, y: firstPoint?.y ?? 0 };
    }
    if (distance >= this.totalPerimeter) {
      const lastPoint = this.points[this.points.length - 1];
      return { x: lastPoint?.x ?? 0, y: lastPoint?.y ?? 0 };
    }

    // Binary search for the segment containing this distance
    let lo = 0;
    let hi = this.points.length - 1;
    while (lo < hi - 1) {
      const mid = (lo + hi) >> 1;
      const midPoint = this.points[mid];
      if (midPoint && midPoint.distance <= distance) {
        lo = mid;
      } else {
        hi = mid;
      }
    }

    const pointA = this.points[lo];
    const pointB = this.points[hi];
    if (!pointA || !pointB) {
      return { x: 0, y: 0 };
    }

    const segmentLength = pointB.distance - pointA.distance;
    if (segmentLength <= 0) {
      return { x: pointA.x, y: pointA.y };
    }

    const fraction = (distance - pointA.distance) / segmentLength;
    return {
      x: pointA.x + (pointB.x - pointA.x) * fraction,
      y: pointA.y + (pointB.y - pointA.y) * fraction,
    };
  }

  // ===========================================================================
  // DRAWING
  // ===========================================================================

  /**
   * Draw the border as a pure polyline (moveTo + lineTo only, no arc()).
   *
   * The visible line occupies the TAIL of the perimeter (from skipDistance
   * to totalPerimeter), so as progress depletes the leading edge advances
   * clockwise.
   */
  private draw(): void {
    const g = this.borderGraphics;
    g.clear();

    if (
      this.displayProgress <= 0 ||
      this.totalPerimeter <= 0 ||
      this.points.length < 2
    ) {
      return;
    }

    const color = this.getColor(this.displayProgress);
    const lineWidth = this.bbs(TIMER_BORDER_BREAKPOINTS.lineWidth);
    const drawDistance = this.displayProgress * this.totalPerimeter;
    const skipDistance = this.totalPerimeter - drawDistance;

    g.setStrokeStyle({ width: lineWidth, color });

    // Find the first polyline point at or after skipDistance
    const startPoint = this.getPointAtDistance(skipDistance);
    g.moveTo(startPoint.x, startPoint.y);

    // Draw through all polyline points after skipDistance
    for (const point of this.points) {
      if (point.distance <= skipDistance) continue;
      g.lineTo(point.x, point.y);
    }

    g.stroke();
  }

  // ===========================================================================
  // COLOR
  // ===========================================================================

  private getColor(progress: number): number {
    for (const threshold of COLOR_THRESHOLDS) {
      if (progress > threshold.above) {
        return threshold.color;
      }
    }
    return 0xf44336;
  }

  // ===========================================================================
  // PARTICLES
  // ===========================================================================

  private createParticles(): void {
    const count = this.bbs(TIMER_BORDER_BREAKPOINTS.particleCount);
    const baseSize = this.bbs(TIMER_BORDER_BREAKPOINTS.particleSize);

    // Create glow (larger semi-transparent circle behind leading particle)
    this.glowGraphic.clear();
    this.glowGraphic.circle(0, 0, baseSize * 2.5);
    this.glowGraphic.fill({ color: 0xffffff, alpha: 0.3 });
    this.glowGraphic.visible = false;

    // Create trailing particles (smallest/faintest first, leading last)
    for (let i = 0; i < count; i++) {
      const sizeMultiplier = 1 - i * PARTICLE_SIZE_DECAY;
      const particleRadius = baseSize * Math.max(0.3, sizeMultiplier);

      const particle = new Graphics();
      particle.circle(0, 0, particleRadius);
      particle.fill(0xffffff);
      particle.alpha = Math.max(0.1, 1 - i * PARTICLE_ALPHA_DECAY);
      particle.visible = false;

      this.particleContainer.addChild(particle);
      this.particles.push(particle);
    }
  }

  private updateParticles(): void {
    if (this.displayProgress <= 0 || this.totalPerimeter <= 0) {
      this.hideParticles();
      return;
    }

    const color = this.getColor(this.displayProgress);
    const drawDistance = this.displayProgress * this.totalPerimeter;
    const skipDistance = this.totalPerimeter - drawDistance;
    const spacing = this.bbs(TIMER_BORDER_BREAKPOINTS.particleSpacing);

    for (let i = 0; i < this.particles.length; i++) {
      const particle = this.particles[i];
      if (!particle) continue;

      // Leading particle at the clockwise-advancing edge, trailing ones follow behind
      const particleDistance = skipDistance + i * spacing;

      if (particleDistance >= this.totalPerimeter) {
        particle.visible = false;
        continue;
      }

      const point = this.getPointAtDistance(particleDistance);
      particle.x = point.x;
      particle.y = point.y;
      particle.tint = color;
      particle.visible = true;

      // Leading particle gets subtle jitter for sparkle
      if (i === 0) {
        particle.x += (Math.random() - 0.5) * 1.2;
        particle.y += (Math.random() - 0.5) * 1.2;
      }
    }

    // Position glow behind leading particle
    const leadingParticle = this.particles[0];
    if (leadingParticle?.visible) {
      const leading = leadingParticle;
      this.glowGraphic.x = leading.x;
      this.glowGraphic.y = leading.y;
      this.glowGraphic.tint = color;
      this.glowGraphic.visible = true;
    } else {
      this.glowGraphic.visible = false;
    }
  }

  private hideParticles(): void {
    for (const particle of this.particles) {
      particle.visible = false;
    }
    this.glowGraphic.visible = false;
  }

  private destroyParticles(): void {
    for (const particle of this.particles) {
      this.particleContainer.removeChild(particle);
      particle.destroy();
    }
    this.particles = [];
  }

  // ===========================================================================
  // TICKER
  // ===========================================================================

  /** Smooth interpolation each frame. */
  private onTick = (): void => {
    if (!this.isAnimating) return;

    // Exponential decay toward target — closes 99% of the gap per second
    const deltaSeconds = Ticker.shared.deltaMS / 1000;
    const lerpFactor = 1 - Math.pow(0.01, deltaSeconds);
    const diff = this.targetProgress - this.displayProgress;

    if (Math.abs(diff) < 0.0005) {
      this.displayProgress = this.targetProgress;
    } else {
      this.displayProgress += diff * lerpFactor;
    }

    this.draw();
    this.updateParticles();
  };
}
